// -----------------------------------------------------------------------
// <copyright file="stringLoader.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------
import { httpUtils } from '../common/apis/httpUtils';
import { addGenevaLog } from './statelessUtil';

const specialMap: { [appLocale: string]: string } = {
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-hk': 'zh-Hant',
  'zh-mo': 'zh-Hant',
  'zh-hans': 'zh-Hans',
  'zh-hant': 'zh-Hant',
  'az-latn-az': 'az-Latn-AZ',
  'be-by': 'be-BY',
  'nb-no': 'no',
  'pt-pt': 'pt-PT',
};

const rtlLanguages: string[] = ['ar', 'arc', 'dv', 'fa', 'ha', 'he', 'khw', 'ks', 'ku', 'ps', 'ur', 'yi'];

let resources: Record<string, string> = null;
let defaultResources: Record<string, string> = null;
let capitalizedResources: Record<string, string> = null;
let capitalizedDefaultResources: Record<string, string> = null;
let localeIsRtl = false;
let currentLocale: string = null;

export const isLoadResourcesEnabled = !!window.emeraldContext && !!window.emeraldContext.isLoadResourcesEnabled;

export const isRtl = (): boolean => localeIsRtl;

const mapLocale = (locale: string): string => {
  let mappedLocale = locale;

  const lowerCaseLocale = locale.toLowerCase();
  if (specialMap[lowerCaseLocale]) {
    mappedLocale = specialMap[lowerCaseLocale];
  } else if (lowerCaseLocale.includes('-')) {
    // just keep the part before the first dash
    mappedLocale = lowerCaseLocale.substring(0, lowerCaseLocale.indexOf('-'));
  }

  return mappedLocale;
};

const capitalizeKeys = (resources: Record<string, string>): Record<string, string> => {
  const capitalizedResources: Record<string, string> = {};
  for (const key in resources) {
    if (resources.hasOwnProperty(key)) {
      capitalizedResources[key.toUpperCase()] = resources[key];
    }
  }

  return capitalizedResources;
};

export const loadResources = async (locale: string, distUrl: string = '/dist'): Promise<void> => {
  let errorMessage = '';
  let errorStack = '';
  try {
    // remove trailing '/' from cdn path
    if (distUrl && distUrl.endsWith('/')) {
      distUrl = distUrl.slice(0, -1);
    }

    if (locale) {
      // We will first map the incoming locale string in order to find the matched json file.
      const mappedLocale = mapLocale(locale);

      // Load English resource if it's not loaded yet, but we only load it once
      if (!defaultResources) {
        // We will load the English one anyway in case the localized json has less entries than English one.
        defaultResources = await httpUtils.get<Record<string, string>>(
          `${distUrl}/assets/strings/Resources.json`,
          null,
          null,
          null,
          3
        );
      }

      // Only load if the locale is not loaded yet.
      if (currentLocale !== mappedLocale) {
        if (mappedLocale.toLowerCase() === 'en') {
          // If the locale is English, just use the default as localized.
          resources = defaultResources;
        } else {
          resources = await httpUtils.get<Record<string, string>>(
            `${distUrl}/assets/strings/Resources.${mappedLocale}.json`,
            null,
            null,
            null,
            3
          );
        }
        localeIsRtl = rtlLanguages.includes(mappedLocale.toLowerCase());
        currentLocale = mappedLocale;
      }
    } else {
      errorMessage = 'locale is null';
      resources = defaultResources;
    }
  } catch (error) {
    errorMessage = error ? error.message : '';
    errorStack = error ? error.stack : '';
    // If the locale is not supported, use default as localized.
    resources = defaultResources;
  } finally {
    // Always prepare the capitalized resources as long as we have resources.
    capitalizedResources = capitalizeKeys(resources);

    // We need capitalized default resources for two reasons:
    // 1. defaultResources may contain the strings that are not translated yet because of the timing of touchdown translation.
    // 2. Deeplink uses English mapped query string as the actual alchemy query string,
    //    so we need to map the English resources (defaultResources) to capitalized ones to align the behavior.
    capitalizedDefaultResources = capitalizeKeys(defaultResources);
  }

  // log exceptions
  if (!!errorMessage) {
    const logHandlerTags = {
      content: {
        contentId: 'loadResources',
        contentName: 'loadResources',
        formnm: 'maxFunnel',
        areaName: 'ErrorBoundary',
        url: `${window.location.href}`,
        errorMessage,
        errorStack,
        locale,
        distUrl,
      },
      behavior: 'OTHER',
    };
    addGenevaLog(logHandlerTags);
  }
};

export const getString = (
  key: string,
  fallbackToEnglish: boolean = true,
  ignoreError: boolean = false,
  returnNullOnError: boolean = false
): string => {
  let text: string = key;
  if (resources && (resources[key] || resources[key] === '')) {
    text = resources[key];
  } else if (key.toUpperCase().includes("DEEPLINK") && capitalizedResources && (capitalizedResources[key.toUpperCase()] || capitalizedResources[key.toUpperCase()] === '')) {
    // Only if the key is a deeplink or directdeeplink, we try to find the capitalized resources.
    // So that we can minimize the impact and avoid the unstablity of other strings mapping.
    text = capitalizedResources[key.toUpperCase()];
  } else if (fallbackToEnglish) {
    text = getEnglishString(key, ignoreError);
  }

  if (text === key && !ignoreError) {
    // logResourceStringLoadError(key, currentLocale);
  }

  if (text === key && returnNullOnError) {
    return null;
  }

  return text;
};

export const getEnglishString = (key: string, ignoreError: boolean = false): string => {
  let text: string = key;
  if (defaultResources && (defaultResources[key] || defaultResources[key] === '')) {
    text = defaultResources[key];
  } else if (key.toUpperCase().includes("DEEPLINK") && capitalizedDefaultResources && (capitalizedDefaultResources[key.toUpperCase()] || capitalizedDefaultResources[key.toUpperCase()] === '')) {
    text = capitalizedDefaultResources[key.toUpperCase()];
  }

  if (text === key && !ignoreError) {
    // logResourceStringLoadError(key, 'en');
  }
  return text;
};
